/**
 * Client copy of the visual indicator rules. The source of truth is simb-common-lib
 * `src/utils/visual-indicator/visual-indicator.ts` (`validate`, `toToken`, `DEFAULT_TOKEN`);
 * the frontend does not depend on the lib, so the few rules the picker needs are copied here.
 * Keep the two in step: app-ms validates again with the lib and answers 400 { reason } on a mismatch.
 */
import type { VisualIndicatorSetting, VisualIndicatorWrap } from "../../types/settings-drawer-interface";

/** The indicator every account had before it was customisable. */
export const DEFAULT_TOKEN = "✨ ";
export const DEFAULT_EMOJI = "✨";

/** SIMB's own "sync in progress" marker; never allowed as an indicator. */
export const LOADING_MARKER = "⌛";

export const TEXT_MAX_LENGTH = 10;

export const VISUAL_INDICATOR_WRAPS: readonly VisualIndicatorWrap[] = ["square", "round", "dot"];

/**
 * V8 (spec): when the indicator changes, do items that already show the old one change too?
 * Still undecided, so the whole behaviour hangs off this one value:
 * - "ask":    the Save dialog offers both choices (the approved mockup, frame 3)
 * - "always": the dialog only confirms the count and time, and existing items are always renamed
 * - "never":  no dialog; only items synced from now on get the new indicator
 */
export type ExistingItemsPolicy = "ask" | "always" | "never";
export const VISUAL_INDICATOR_EXISTING_ITEMS: ExistingItemsPolicy = "ask";

/** Throughput used for the time estimates (spec §5.4): about 300 renames a minute. */
export const RENAMES_PER_MINUTE = 300;
export const estimateMinutes = (items: number): number => Math.max(1, Math.ceil(items / RENAMES_PER_MINUTE));

export type VisualIndicatorInvalidReason =
  | "invalid_kind"
  | "empty"
  | "not_single_emoji"
  | "reserved"
  | "too_long"
  | "invalid_characters"
  | "invalid_wrap";

export type VisualIndicatorValidation =
  | { ok: true; value: VisualIndicatorSetting }
  | { ok: false; reason: VisualIndicatorInvalidReason };

const TEXT_PATTERN = /^[\p{L}\p{N} _&+#-]+$/u;
// Flags are pairs of regional indicators and keycaps end in U+20E3; neither is Extended_Pictographic.
const EMOJI_PATTERN = /\p{Extended_Pictographic}|\p{Regional_Indicator}|⃣/u;

export const graphemes = (value: string): string[] => {
  const Segmenter = (Intl as any)?.Segmenter;
  if (typeof Segmenter === "function") {
    return Array.from(new Segmenter(undefined, { granularity: "grapheme" }).segment(value), (s: any) => s.segment as string);
  }
  return Array.from(value);
};

export const validate = (input: unknown): VisualIndicatorValidation => {
  const candidate = (input ?? {}) as Partial<VisualIndicatorSetting>;

  if (candidate.kind !== "emoji" && candidate.kind !== "text") return { ok: false, reason: "invalid_kind" };

  const value = typeof candidate.value === "string" ? candidate.value.trim() : "";
  if (!value) return { ok: false, reason: "empty" };
  if (value.includes(LOADING_MARKER)) return { ok: false, reason: "reserved" };

  if (candidate.kind === "emoji") {
    const parts = graphemes(value);
    if (parts.length !== 1 || !EMOJI_PATTERN.test(parts[0])) return { ok: false, reason: "not_single_emoji" };
    return { ok: true, value: { kind: "emoji", value } };
  }

  const wrap = candidate.wrap ?? "square";
  if (!VISUAL_INDICATOR_WRAPS.includes(wrap)) return { ok: false, reason: "invalid_wrap" };
  if (graphemes(value).length > TEXT_MAX_LENGTH) return { ok: false, reason: "too_long" };
  if (!TEXT_PATTERN.test(value)) return { ok: false, reason: "invalid_characters" };

  return { ok: true, value: { kind: "text", value, wrap } };
};

/** A text label in its wrapper, with no trailing space: "[SYNC]", "(SYNC)", "SYNC ·". */
export const wrapLabel = (value: string, wrap: VisualIndicatorWrap = "square"): string => {
  if (wrap === "round") return `(${value})`;
  if (wrap === "dot") return `${value} ·`;
  return `[${value}]`;
};

/** The exact prefix SIMB writes into the name. A missing or invalid setting means the default ✨. */
export const toToken = (setting?: VisualIndicatorSetting | null): string => {
  if (!setting) return DEFAULT_TOKEN;
  const result = validate(setting);
  if (!result.ok) return DEFAULT_TOKEN;
  const { kind, value, wrap } = result.value;
  return kind === "emoji" ? `${value} ` : `${wrapLabel(value, wrap)} `;
};

/** How the indicator reads in copy: the token without its trailing space ("✨", "[SYNC]"). */
export const displayToken = (token: string): string => token.replace(/ $/, "");

export const PRESET_EMOJIS: ReadonlyArray<{ emoji: string; label: string }> = [
  { emoji: "✨", label: "Sparkles (default)" },
  { emoji: "🔗", label: "Link" },
  { emoji: "🔄", label: "Arrows" },
  { emoji: "📌", label: "Pin" },
  { emoji: "⭐", label: "Star" },
  { emoji: "🟢", label: "Green circle" },
  { emoji: "🧩", label: "Puzzle piece" },
  { emoji: "🔁", label: "Repeat" },
];

export const INVALID_REASON_COPY: Record<VisualIndicatorInvalidReason, string> = {
  invalid_kind: "Choose an emoji or a label.",
  empty: "Type a label.",
  not_single_emoji: "Pick a single emoji.",
  reserved: "⌛ is reserved for syncs in progress.",
  too_long: `Use up to ${TEXT_MAX_LENGTH} characters.`,
  invalid_characters: "Use letters, numbers, spaces and - _ & + # only.",
  invalid_wrap: "Choose how the label is shown.",
};

export const formatCount = (value: number): string => Number(value || 0).toLocaleString("en-US");
